import React, { FC, ReactText, useCallback, useMemo, useRef } from 'react';
import { Dropdown, MenuProps } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { PaginationConfig } from 'antd/lib/pagination';
import { SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface';
import moment from 'moment';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { exportReportToExcel, exportReportToPdf } from '@core/utils/methods';
import {
  DatesAtAGlanceReportResponse,
  DatesAtAGlanceRow,
  OptionProps,
  ReportsFilters,
} from '@models/interfaces';
import { CustomTable } from '@shared/components';
import { PrimaryButton } from '@shared/modules';
import type { ReportsSummaryMetric } from '../../models';
import {
  DATES_AT_A_GLANCE_TIMELINE_SERIES,
  FULL_FILTER_CAPTION,
  getDatesAtAGlanceColumns,
  getDatesAtAGlanceReportRowClassName,
  getDatesAtAGlanceReportRowKey,
  getDatesAtAGlanceRowRedirectUrl,
  getDatesAtAGlanceTimelineChartData,
  hasDatesAtAGlanceTimelineData,
  isInteractiveRowClick,
  openReportRowInNewTab,
} from '../../models';
import { ReportChartCard } from '../report-chart-card';
import { ReportsSummary } from '../reports-summary';

import './styles.scss';

interface DatesAtAGlanceReportProps {
  data?: DatesAtAGlanceReportResponse;
  filters: ReportsFilters;
  onTableChange: (
    pagination: PaginationConfig,
    tableFilters: Record<string, ReactText[] | null>,
    sorter: SorterResult<Store> | SorterResult<Store>[],
    extra: TableCurrentDataSource<Store>,
  ) => void;
  sortByOptions: OptionProps[];
}

export const DatesAtAGlanceReport: FC<DatesAtAGlanceReportProps> = ({
  data,
  filters,
  onTableChange,
  sortByOptions,
}: DatesAtAGlanceReportProps) => {
  const chartsRef = useRef<HTMLDivElement>(null);
  const rows = data?.table.items ?? [];
  const total = data?.table.total ?? 0;
  const timelineBuckets = data?.charts.timelineBuckets ?? [];
  const timelineChartData = useMemo(
    () => getDatesAtAGlanceTimelineChartData(timelineBuckets),
    [timelineBuckets],
  );
  const columns = useMemo(
    () => getDatesAtAGlanceColumns(filters, sortByOptions),
    [filters, sortByOptions],
  );
  const metrics: ReportsSummaryMetric[] = [
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Total Milestones',
      value: data?.summary.totalMilestones ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Completed Milestones',
      value: data?.summary.completedMilestones ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Overdue Milestones',
      value: data?.summary.overdueMilestones ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Upcoming Milestones',
      value: data?.summary.upcomingMilestones ?? 0,
    },
  ];

  const onRowClick = useCallback(
    (
      record: DatesAtAGlanceRow,
      event: { target?: EventTarget | null },
    ): void => {
      const redirectUrl = getDatesAtAGlanceRowRedirectUrl(record);

      if (!redirectUrl || isInteractiveRowClick(event)) {
        return;
      }

      openReportRowInNewTab(redirectUrl);
    },
    [],
  );

  const onExportExcel = useCallback((): void => {
    exportReportToExcel({
      columns,
      fileName: `Dates-At-A-Glance-${moment().format('YYYY-MM-DD-HHmm')}.xlsx`,
      metrics,
      reportTitle: 'Dates At A Glance',
      rows,
    });
  }, [columns, metrics, rows]);

  const onExportPdf = useCallback((): void => {
    exportReportToPdf({
      chartsElement: chartsRef.current,
      columns,
      fileName: `Dates-At-A-Glance-${moment().format('YYYY-MM-DD-HHmm')}.pdf`,
      metrics,
      reportTitle: 'Dates At A Glance',
      rows,
    });
  }, [columns, metrics, rows]);

  const exportMenu: MenuProps = {
    items: [
      { key: 'excel', label: 'Export as Excel', onClick: onExportExcel },
      { key: 'pdf', label: 'Export as PDF', onClick: onExportPdf },
    ],
  };

  return (
    <div className="prov-dates-at-a-glance-report">
      <div className="prov-dates-at-a-glance-report__toolbar">
        <p className="prov-dates-at-a-glance-report__description">
          Milestone-level view of every PACE task across projects, timed by
          actual date (if completed) or forecasted date (if not). Use it to see
          what&apos;s landing in a given window, completed or upcoming.
        </p>
        <Dropdown disabled={!rows.length} menu={exportMenu}>
          <PrimaryButton title="Export ▾" />
        </Dropdown>
      </div>

      <ReportsSummary metrics={metrics} />

      <div className="prov-dates-at-a-glance-report__charts" ref={chartsRef}>
        <ReportChartCard
          hasData={hasDatesAtAGlanceTimelineData(timelineChartData)}
          title="Milestone Status by Month"
        >
          <div className="prov-dates-at-a-glance-report__timeline-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={timelineChartData}
                margin={{ bottom: 8, left: 8, right: 24, top: 8 }}
              >
                <CartesianGrid stroke="rgba(255,255,255,0.12)" />
                <XAxis dataKey="label" stroke="rgba(255,255,255,0.65)" />
                <YAxis allowDecimals={false} stroke="rgba(255,255,255,0.65)" />
                <Tooltip cursor={false} />
                <Legend />
                {DATES_AT_A_GLANCE_TIMELINE_SERIES.map(
                  ({ dataKey, fill, name }) => (
                    <Bar
                      dataKey={dataKey}
                      fill={fill}
                      key={dataKey}
                      name={name}
                      stackId="milestones"
                    />
                  ),
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ReportChartCard>
      </div>

      <CustomTable
        className="prov-dates-at-a-glance-report__table"
        columns={columns}
        dataSource={rows}
        onChange={onTableChange}
        onRowClick={onRowClick}
        pagination={{
          current: filters.page,
          pageSize: filters.size,
          total,
        }}
        rowClassName={getDatesAtAGlanceReportRowClassName}
        rowKey={getDatesAtAGlanceReportRowKey}
        scroll={{ x: 3600, y: 'auto' }}
      />
    </div>
  );
};
